import { useEffect, useRef } from 'react'

import { get } from '~/api/fetch-client'
import { planInterstitial, resolveInterstitialSlot } from '~/components/player/interstitial-logic'
import type { PlayerState, QueueItem } from '~/components/player/types'
import { queueItemUid } from '~/components/player/utils'
import { useAppStore } from '~/hooks/use-app-store'

interface InterstitialResponse {
  id: number
  speakerId: string
  script: string
  duration: number
  streamUrl: string
}

// Cap on a single currentTime jump counted as playback. Normal timeupdate steps
// are a fraction of a second; a larger jump is a seek or track change, not
// listening, so it's excluded rather than accumulated.
const MAX_PLAYED_DELTA_SECONDS = 3

function randomInterval(minMinutes: number, maxMinutes: number): number {
  return (minMinutes + Math.random() * (maxMinutes - minMinutes)) * 60
}

interface UseInterstitialModeOptions {
  state: PlayerState
  insertAt: (index: number, tracks: QueueItem[]) => void
  subscribeCurrentTime: (cb: () => void) => () => void
  getCurrentTime: () => number
}

export function useInterstitialMode({
  state,
  insertAt,
  subscribeCurrentTime,
  getCurrentTime,
}: UseInterstitialModeOptions) {
  const { queue, currentIndex, radioMode } = state
  const interstitialsEnabled = useAppStore((s) => s.playerSettings.interstitialsEnabled)
  const minMinutes = useAppStore((s) => s.playerSettings.interstitialMinMinutes)
  const maxMinutes = useAppStore((s) => s.playerSettings.interstitialMaxMinutes)

  const accumulatedSecondsRef = useRef(0)
  const thresholdRef = useRef(randomInterval(minMinutes ?? 30, maxMinutes ?? 40))
  const prevIndexRef = useRef(currentIndex)
  const lastTimeRef = useRef(0)
  // Single-flight guard: synthesis (LLM script + TTS) is slow, and the radio
  // buffer mutates `queue` repeatedly while it runs. Without this, every track
  // advance past the threshold would fire another overlapping request, landing
  // several breaks back-to-back.
  const fetchingRef = useRef(false)

  const active = radioMode && interstitialsEnabled
  // The script is written for a fixed slot, but synthesis is slow enough that the
  // playhead moves while it runs. Resolve placement against live state (not the
  // fire-time snapshot) so the break airs where its script actually applies.
  const stateRef = useRef(state)
  stateRef.current = state
  const activeRef = useRef(active)
  activeRef.current = active

  // Accumulate real listened time from the audio element's currentTime progression
  // (not track duration) so skipping through a long DJ set only counts the part
  // actually heard, and background tabs stay accurate (currentTime keeps advancing
  // even when timers are throttled).
  useEffect(() => {
    if (!active) return
    lastTimeRef.current = getCurrentTime()
    return subscribeCurrentTime(() => {
      const s = stateRef.current
      const now = getCurrentTime()
      const delta = now - lastTimeRef.current
      lastTimeRef.current = now
      if (delta <= 0 || delta > MAX_PLAYED_DELTA_SECONDS) return
      if (!s.isPlaying) return
      if (s.queue[s.currentIndex]?.isInterstitial) return
      accumulatedSecondsRef.current += delta
    })
  }, [active, subscribeCurrentTime, getCurrentTime])

  // Fire at a track boundary once enough listening has accumulated, so the break
  // lands cleanly between tracks (via the anchor) rather than mid-song.
  useEffect(() => {
    if (!active) return
    if (currentIndex === prevIndexRef.current) return
    prevIndexRef.current = currentIndex

    if (accumulatedSecondsRef.current < thresholdRef.current) return
    if (fetchingRef.current) return

    const plan = planInterstitial(queue, currentIndex)
    if (!plan) return

    // A break is due: restart the cadence timer now, at the trigger — not when
    // synthesis/placement finishes. The interval measures listening time between
    // breaks, so it must clear here regardless of how long synthesis takes or
    // whether the break ends up placeable. A dropped break is simply skipped; the
    // next comes after another full interval, never back-to-back.
    accumulatedSecondsRef.current = 0
    thresholdRef.current = randomInterval(minMinutes ?? 30, maxMinutes ?? 40)

    const params = new URLSearchParams()
    for (const id of plan.pastIds) params.append('trackId', String(id))
    for (const id of plan.nextIds) params.append('nextId', String(id))

    fetchingRef.current = true
    get<InterstitialResponse>(`/interstitials/next?${params.toString()}`)
      .then((interstitial) => {
        if (!activeRef.current) return

        // Place the break right after its anchor in the *current* queue. If the
        // playhead already passed the anchor (synthesis outran the lookahead),
        // drop it rather than airing a recap/preview that no longer matches. The
        // cadence timer already reset at the trigger, so a drop just skips this
        // break — the next arrives after a fresh interval.
        const live = stateRef.current
        const slot = resolveInterstitialSlot(live.queue, live.currentIndex, plan.anchorId)
        if (slot === null) return

        const item: QueueItem = {
          uid: queueItemUid(),
          id: interstitial.id,
          title: 'DJ Break',
          artist: interstitial.speakerId,
          artistId: 0,
          album: '',
          albumId: 0,
          duration: interstitial.duration,
          coverArt: null,
          replayGain: null,
          tempo: null,
          keyName: null,
          energy: null,
          mood: null,
          isInterstitial: true,
          streamOverride: interstitial.streamUrl,
        }
        insertAt(slot, [item])
      })
      .catch((err: unknown) => {
        console.error('[Interstitials] Fetch failed:', err)
      })
      .finally(() => {
        fetchingRef.current = false
      })
  }, [currentIndex, active, queue, insertAt, minMinutes, maxMinutes])

  useEffect(() => {
    if (!active) {
      accumulatedSecondsRef.current = 0
      thresholdRef.current = randomInterval(minMinutes ?? 30, maxMinutes ?? 40)
      prevIndexRef.current = -1
      fetchingRef.current = false
    }
  }, [active, minMinutes, maxMinutes])
}
